import {
  APP_ID,
  ApplicationConfig,
  LOCALE_ID,
  importProvidersFrom,
  ErrorHandler,
  provideZonelessChangeDetection,
  APP_INITIALIZER,
} from '@angular/core';
import { Router } from '@angular/router';
import { provideAnimations } from '@angular/platform-browser/animations';
import { RouteReuseStrategy, provideRouter, withPreloading } from '@angular/router';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  HttpClient,
  provideHttpClient,
  withInterceptorsFromDi,
  withFetch,
  HTTP_INTERCEPTORS,
  withInterceptors,
} from '@angular/common/http';
import { MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { DatePipe, registerLocaleData } from '@angular/common';
import localePt from '@angular/common/locales/pt';
import localeEn from '@angular/common/locales/en';
import localeEs from '@angular/common/locales/es';
import localeFr from '@angular/common/locales/fr';
import localeDO from '@angular/common/locales/es-DO';
import { MatDialog } from '@angular/material/dialog';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS, MatFormFieldDefaultOptions } from '@angular/material/form-field';
import { provideNgxMask } from 'ngx-mask';
import { provideDotLottie } from 'ngx-lottie/dotlottie-web';
import * as Sentry from '@sentry/angular';

import { DeferredSentryErrorHandler } from './core/helpers/deferred-sentry-error-handler';
import { routes } from './app.routes';
import { HttpLoaderFactory } from './core/helpers/translate-loader-factory';
import { provideInitializeStorageApp } from './core/providers/initialize-storage-app.provider';
import { provideInitPlatformId } from './core/providers/initialize-platform-id.provider';
import { provideInitCompany } from './core/providers/initialize-company.provider';
import { providePageVisibilityRefresh } from './core/providers/provide-page-visibility-refresh.provider';
import { CustomReuseStrategy } from './core/helpers/route-reuse.strategy';
import { PreloadStrategy } from './core/helpers/preload-strategy';
import { CustomDateFormat } from './components/date-picker/custom-date-format';
import { LocaleService } from './core/services/locale.service';
import { TokenInterceptor } from './core/helpers/tokenInterceptor';
import { UnauthorizedInterceptor } from './core/helpers/unauthorizedInterceptor';
import { KycInterceptor } from './core/helpers/kyc-interceptor.service';
import { ErrorInterceptor } from './core/helpers/error-interceptor.service';
import { PaginatorIntlService } from './core/services/paginator-intl.service';
import { StorageService } from './core/services/storage.service';
import { getBrowserLanguage } from './core/helpers/storage-utils';
import { CustomMatDialog } from './core/wrapper/dialog-wrapper';
import { sourceInterceptor } from '~/core/helpers/source.interceptor';

const matFormFieldDefaults: MatFormFieldDefaultOptions = {
  appearance: 'fill',
  floatLabel: 'auto',
  subscriptSizing: 'dynamic',
  hideRequiredMarker: false,
};

registerLocaleData(localePt);
registerLocaleData(localeEn);
registerLocaleData(localeEs);
registerLocaleData(localeFr);
registerLocaleData(localeDO);

export const appConfig: ApplicationConfig = {
  providers: [
    provideZonelessChangeDetection(),
    provideAnimations(),
    provideRouter(routes, withPreloading(PreloadStrategy)),
    provideHttpClient(withFetch(), withInterceptorsFromDi(), withInterceptors([sourceInterceptor])),
    importProvidersFrom([
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpClient],
        },
      }),
    ]),
    { provide: APP_ID, useValue: 'bet-plus-web' },
    { provide: RouteReuseStrategy, useClass: CustomReuseStrategy },
    { provide: MatDialog, useClass: CustomMatDialog },
    {
      provide: MAT_DATE_LOCALE,
      useFactory: (localeService: LocaleService) => localeService.getLocale(),
      deps: [LocaleService],
    },
    { provide: MAT_DATE_FORMATS, useClass: CustomDateFormat },
    { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: matFormFieldDefaults },
    {
      provide: LOCALE_ID,
      useFactory: (storageService: StorageService) => storageService.getLanguage() || getBrowserLanguage(),
      deps: [StorageService],
    },
    { provide: HTTP_INTERCEPTORS, useClass: TokenInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: UnauthorizedInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: KycInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    {
      provide: MatPaginatorIntl,
      useFactory: (translate) => {
        const service = new PaginatorIntlService();
        service.injectTranslateService(translate);
        return service;
      },
      deps: [TranslateService],
    },
    // Customs Providers
    provideInitializeStorageApp(),
    provideInitPlatformId(),
    provideInitCompany(),
    providePageVisibilityRefresh(),
    provideNgxMask(),
    provideDotLottie({ player: () => import('@lottiefiles/dotlottie-web').then((m) => m.DotLottie) }),
    DatePipe,
    {
      provide: ErrorHandler,
      useFactory: () => new DeferredSentryErrorHandler(),
    },
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler(),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      deps: [Sentry.TraceService],
      multi: true,
    },
  ],
};
